import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PageContent } from '../components/Layout'
import { ColumnConfiguration, useViewSettings } from '../hooks/useViewSettings'
import { makeApiCall, makeJsonApiCall, getAuthHeaders } from '../utils/customerUtils'
import CustomColumnEditor from '../components/CustomColumnEditor'

interface CustomColumn {
  id?: number
  key: string
  name: string
  type: 'text' | 'number' | 'date' | 'dropdown' | 'boolean'
  visible: boolean
  order: number
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  resizable?: boolean
  required?: boolean
  defaultValue?: string
  dropdownOptions?: string[]
  isCustom: boolean
}

const ColumnSettings: React.FC = () => {
  const { t } = useTranslation()
  const { 
    settings: viewSettings, 
    updateColumnConfigurations,
    resetToDefaultColumns 
  } = useViewSettings('deals_board')

  const [columns, setColumns] = useState<CustomColumn[]>([])
  const [showAddColumn, setShowAddColumn] = useState(false)
  const [editingColumn, setEditingColumn] = useState<CustomColumn | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [migrating, setMigrating] = useState(false)
  const [databaseStatus, setDatabaseStatus] = useState<any>(null)

  // Default system columns
  const defaultColumns: CustomColumn[] = [
    { key: 'name', name: t('deals.columnSettings.columnNames.dealName'), type: 'text', visible: true, order: 0, align: 'left', sortable: true, resizable: true, isCustom: false },
    { key: 'customer_name', name: t('deals.columnSettings.columnNames.customer'), type: 'text', visible: true, order: 1, align: 'left', sortable: true, resizable: true, isCustom: false },
    { key: 'amount', name: t('deals.columnSettings.columnNames.amount'), type: 'number', visible: true, order: 2, align: 'right', sortable: true, resizable: true, isCustom: false },
    { key: 'deal_type', name: t('deals.columnSettings.columnNames.type'), type: 'text', visible: true, order: 3, align: 'center', sortable: true, resizable: true, isCustom: false },
    { key: 'net_commission', name: t('deals.columnSettings.columnNames.netCommission'), type: 'number', visible: true, order: 4, align: 'right', sortable: true, resizable: true, isCustom: false },
    { key: 'expected_close_date', name: t('deals.columnSettings.columnNames.expectedClose'), type: 'date', visible: true, order: 5, align: 'left', sortable: true, resizable: true, isCustom: false },
    { key: 'status_name', name: t('deals.columnSettings.columnNames.status'), type: 'text', visible: false, order: 6, align: 'left', sortable: true, resizable: true, isCustom: false },
    { key: 'created_at', name: t('deals.columnSettings.columnNames.created'), type: 'date', visible: false, order: 7, align: 'left', sortable: true, resizable: true, isCustom: false }
  ]

  useEffect(() => {
    fetchCustomColumns()
  }, [])

  const checkDatabaseStatus = async () => {
    try {
      console.log('🔍 Checking database status...')
      const response = await makeApiCall('/api/custom-columns/status', {
        method: 'GET',
        headers: getAuthHeaders()
      })

      console.log('📡 Database status response:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        url: response.url
      })

      if (response.ok) {
        const status = await response.json()
        setDatabaseStatus(status)
        console.log('✅ Database status data:', {
          ...status,
          timestamp: new Date().toISOString()
        })
        
        // Additional debugging for table existence
        if (status.table_exists === false) {
          console.warn('⚠️ custom_columns table does not exist - migration needed')
        }
        if (status.column_count === 0) {
          console.warn('⚠️ custom_columns table exists but is empty')
        }
        
        return status
      } else {
        const errorText = await response.text().catch(() => 'Unknown error')
        console.error('❌ Database status check failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        })
        setDatabaseStatus({ database: 'error', message: `Database connection failed (${response.status})` })
        return null
      }
    } catch (error) {
      console.error('🚨 Database status check exception:', {
        error: error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      })
      setDatabaseStatus({ database: 'error', message: 'Database service unavailable' })
      return null
    }
  }

  const fetchCustomColumns = async () => {
    try {
      setLoading(true)
      console.log('Fetching custom columns...')
      
      // Check database status first
      const status = await checkDatabaseStatus()
      if (!status || status.database !== 'ready') {
        console.group('🔧 Database not ready - using fallback mode')
        console.log('Status object:', status)
        console.log('Database state:', status?.database)
        console.log('Message:', status?.message || 'Database unavailable')
        console.log('Table exists:', status?.table_exists)
        console.log('Column count:', status?.column_count)
        
        // Use default columns but still apply any saved view settings so UI reflects current configuration
        const allColumns = [...defaultColumns]
        console.log('Default columns loaded:', allColumns.map(col => ({ key: col.key, name: col.name, visible: col.visible, order: col.order })))
        
        if (viewSettings) {
          console.log('View settings available:', viewSettings)
          
          if (viewSettings.columnConfigurations && Array.isArray(viewSettings.columnConfigurations)) {
            console.log('Applying columnConfigurations:', viewSettings.columnConfigurations)
            const configMap = new Map(viewSettings.columnConfigurations.map(cfg => [cfg.key, cfg]))
            for (const col of allColumns) {
              const cfg = configMap.get(col.key)
              if (cfg) {
                console.log(`Applying config for ${col.key}:`, cfg)
                col.visible = cfg.visible
                col.order = typeof cfg.order === 'number' ? cfg.order : col.order
                col.align = (cfg.align as any) || col.align
                col.sortable = cfg.sortable ?? col.sortable
                col.resizable = cfg.resizable ?? col.resizable
              }
            }
          } else if (viewSettings.tableColumns && Array.isArray(viewSettings.tableColumns)) {
            console.log('Applying tableColumns:', viewSettings.tableColumns)
            const orderIndex = new Map(viewSettings.tableColumns.map((key, idx) => [key, idx]))
            for (const col of allColumns) {
              if (orderIndex.has(col.key)) {
                col.visible = true
                col.order = orderIndex.get(col.key) as number
                console.log(`Set ${col.key} visible=true, order=${col.order}`)
              } else {
                col.visible = false
                console.log(`Set ${col.key} visible=false`)
              }
            }
          }
        } else {
          console.log('No view settings available')
        }
        
        allColumns.sort((a, b) => a.order - b.order)
        console.log('Final columns after fallback:', allColumns.map(col => ({ key: col.key, name: col.name, visible: col.visible, order: col.order })))
        console.groupEnd()
        
        setColumns(allColumns)
        setLoading(false)
        return
      }

      console.log('🔄 Fetching custom columns from API...')
      const response = await makeApiCall('api/custom-columns/deals')
      
      console.log('📡 Custom columns API response:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        url: response.url
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ Custom columns response data:', data)
        const customColumns = data.columns || []
        console.log('📊 Custom columns array:', customColumns)
        
        // Merge default columns with custom columns
        const allColumns = [...defaultColumns]
        customColumns.forEach((customCol: any) => {
          // Safely parse dropdown options
          let dropdownOptions: string[] = []
          if (customCol.dropdown_options) {
            // Handle different data types
            if (Array.isArray(customCol.dropdown_options)) {
              // Already an array
              dropdownOptions = customCol.dropdown_options
            } else if (typeof customCol.dropdown_options === 'string') {
              try {
                // Try to parse as JSON first
                dropdownOptions = JSON.parse(customCol.dropdown_options)
              } catch (error) {
                // If JSON parsing fails, treat as comma-separated string
                console.warn('Invalid JSON for dropdown options, parsing as comma-separated:', customCol.dropdown_options)
                dropdownOptions = customCol.dropdown_options.split(',').map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0)
              }
            } else {
              console.warn('Unexpected dropdown_options type:', typeof customCol.dropdown_options, customCol.dropdown_options)
            }
          }

          allColumns.push({
            id: customCol.id,
            key: customCol.column_key,
            name: customCol.column_name,
            type: customCol.column_type,
            visible: customCol.visible,
            order: customCol.order_position,
            align: customCol.text_align,
            sortable: customCol.sortable,
            resizable: customCol.resizable,
            required: customCol.required,
            defaultValue: customCol.default_value,
            dropdownOptions,
            isCustom: true
          })
        })
        
        // Apply existing view settings to reflect user's current configuration
        if (viewSettings) {
          if (viewSettings.columnConfigurations && Array.isArray(viewSettings.columnConfigurations)) {
            const configMap = new Map(viewSettings.columnConfigurations.map(cfg => [cfg.key, cfg]))
            for (const col of allColumns) {
              const cfg = configMap.get(col.key)
              if (cfg) {
                col.visible = cfg.visible
                col.order = typeof cfg.order === 'number' ? cfg.order : col.order
                col.align = (cfg.align as any) || col.align
                col.sortable = cfg.sortable ?? col.sortable
                col.resizable = cfg.resizable ?? col.resizable
              }
            }
          } else if (viewSettings.tableColumns && Array.isArray(viewSettings.tableColumns)) {
            const orderIndex = new Map(viewSettings.tableColumns.map((key, idx) => [key, idx]))
            for (const col of allColumns) {
              if (orderIndex.has(col.key)) {
                col.visible = true
                col.order = orderIndex.get(col.key) as number
              } else {
                col.visible = false
              }
            }
          }
        }

        // Sort by order (after applying settings)
        allColumns.sort((a, b) => a.order - b.order)
        setColumns(allColumns)
      } else {
        // If API fails, use default columns
        setColumns(defaultColumns)
      }
    } catch (error) {
      console.error('Error fetching custom columns:', error)
      setColumns(defaultColumns)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveColumns = async () => {
    try {
      setSaving(true)
      
      // Save custom columns to backend
      const customColumns = columns.filter(col => col.isCustom)
      // Map to backend schema
      const response = await makeJsonApiCall('/api/custom-columns/deals', 'PUT', {
        columns: customColumns.map(col => ({
          id: col.id,
          column_key: col.key,
          column_name: col.name,
          column_type: col.type,
          visible: col.visible,
          order_position: col.order,
          text_align: col.align,
          sortable: col.sortable,
          resizable: col.resizable,
          required: col.required,
          default_value: col.defaultValue,
          dropdown_options: col.dropdownOptions ?? null
        }))
      })

      if (response.ok) {
        // Update view settings
        const columnConfigs: ColumnConfiguration[] = columns.map(col => ({
          key: col.key,
          name: col.name,
          visible: col.visible,
          order: col.order,
          align: col.align,
          sortable: col.sortable,
          resizable: col.resizable
        }))
        
        updateColumnConfigurations(columnConfigs)
        
        // Refresh columns
        await fetchCustomColumns()
        alert('Column settings saved successfully!')
      } else {
        throw new Error('Failed to save columns')
      }
    } catch (error) {
      console.error('Error saving columns:', error)
      alert('Failed to save column settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleRunMigration = async () => {
    try {
      setMigrating(true)
      console.log('🚀 Triggering database migration...')
      
      const response = await makeApiCall('/api/custom-columns/migrate', {
        method: 'POST',
        headers: getAuthHeaders()
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ Migration completed:', data.message)
        // Refresh the page to reload with new database state
        window.location.reload()
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Migration failed' }))
        throw new Error(errorData.details || errorData.error || 'Migration failed')
      }
    } catch (error) {
      console.error('❌ Migration failed:', error)
      alert('Migration failed: ' + (error instanceof Error ? error.message : String(error)))
    } finally {
      setMigrating(false)
    }
  }

  const handleAddColumn = async (newColumn: CustomColumn) => {
    try {
      setSaving(true)
      const maxOrder = Math.max(...columns.map(col => col.order), -1)
      const columnWithOrder = { ...newColumn, order: maxOrder + 1, isCustom: true }
      
      // Save to backend first
      const requestData = {
        column_key: columnWithOrder.key,
        column_name: columnWithOrder.name,
        column_type: columnWithOrder.type,
        visible: columnWithOrder.visible,
        order_position: columnWithOrder.order,
        text_align: columnWithOrder.align,
        sortable: columnWithOrder.sortable,
        resizable: columnWithOrder.resizable,
        required: columnWithOrder.required,
        default_value: columnWithOrder.defaultValue,
        dropdown_options: columnWithOrder.dropdownOptions ?? null
      }
      console.log('Creating column with data:', requestData)
      const response = await makeJsonApiCall('/api/custom-columns/deals', 'POST', requestData)

      if (response.ok) {
        await fetchCustomColumns() // Refresh the list
        setShowAddColumn(false)
      } else {
        // Get more detailed error information
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error('API Error:', response.status, errorData)
        console.error('Request data was:', requestData)
        
        // Handle specific error cases
        if (response.status === 400 && errorData.error === 'Column key already exists') {
          throw new Error(`Column key "${requestData.column_key}" already exists. Please use a different name or key.`)
        }
        
        if (response.status === 500 && errorData.error?.includes('Database table not found')) {
          throw new Error('Database migration required. Please contact support or run database migrations.')
        }
        
        const errorMessage = errorData.details || errorData.error || response.statusText
        throw new Error(`Failed to create column: ${errorMessage}`)
      }
    } catch (error) {
      console.error('Error adding column:', error)
      // Show more detailed error message
      alert(`Failed to add column: ${error instanceof Error ? error.message : 'Please try again.'}`)
    } finally {
      setSaving(false)
    }
  }

  const handleEditColumn = async (updatedColumn: CustomColumn) => {
    try {
      setSaving(true)
      
      if (updatedColumn.id) {
        // Update existing column
        const response = await makeJsonApiCall(`/api/custom-columns/deals/${updatedColumn.id}`, 'PUT', {
          column_name: updatedColumn.name,
          visible: updatedColumn.visible,
          order_position: updatedColumn.order,
          text_align: updatedColumn.align,
          sortable: updatedColumn.sortable,
          resizable: updatedColumn.resizable,
          required: updatedColumn.required,
          default_value: updatedColumn.defaultValue,
          dropdown_options: updatedColumn.dropdownOptions ?? null
        })

        if (response.ok) {
          await fetchCustomColumns() // Refresh the list
          setEditingColumn(null)
        } else {
          throw new Error('Failed to update column')
        }
      }
    } catch (error) {
      console.error('Error updating column:', error)
      alert('Failed to update column. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteColumn = async (column: CustomColumn) => {
    if (confirm('Are you sure you want to delete this column? All data in this column will be lost.')) {
      try {
        setSaving(true)
        
        if (column.id) {
          const response = await makeApiCall(`/api/custom-columns/deals/${column.id}`, {
            method: 'DELETE'
          })
          
          if (response.ok) {
            await fetchCustomColumns() // Refresh the list
          } else {
            throw new Error('Failed to delete column')
          }
        } else {
          // Remove from local state if not saved to backend yet
          setColumns(columns.filter(col => col.key !== column.key))
        }
      } catch (error) {
        console.error('Error deleting column:', error)
        alert('Failed to delete column. Please try again.')
      } finally {
        setSaving(false)
      }
    }
  }

  const handleToggleColumn = (columnKey: string) => {
    setColumns(columns.map(col => 
      col.key === columnKey ? { ...col, visible: !col.visible } : col
    ))
  }

  const handleReorderColumn = (columnKey: string, direction: 'up' | 'down') => {
    const currentIndex = columns.findIndex(col => col.key === columnKey)
    if (currentIndex === -1) return

    const newColumns = [...columns]
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

    if (targetIndex >= 0 && targetIndex < newColumns.length) {
      // Swap order values
      const currentOrder = newColumns[currentIndex].order
      newColumns[currentIndex].order = newColumns[targetIndex].order
      newColumns[targetIndex].order = currentOrder

      // Sort by order
      newColumns.sort((a, b) => a.order - b.order)
      setColumns(newColumns)
    }
  }

  const handleResetToDefaults = async () => {
    if (confirm('Are you sure you want to reset to default columns? This will remove all custom columns.')) {
      try {
        setSaving(true)
        
        // Delete all custom columns from backend
        const response = await makeApiCall('/api/custom-columns/deals', {
          method: 'DELETE'
        })
        
        if (response.ok) {
          resetToDefaultColumns()
          setColumns(defaultColumns)
          alert('Column settings reset to defaults!')
        } else {
          throw new Error('Failed to reset columns')
        }
      } catch (error) {
        console.error('Error resetting columns:', error)
        alert('Failed to reset column settings. Please try again.')
      } finally {
        setSaving(false)
      }
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('deals.columnSettings.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('deals.columnSettings.subtitle')}
            </p>
            {databaseStatus && databaseStatus.database !== 'ready' && (
              <div className={`mt-3 p-3 rounded-md border ${
                databaseStatus.database === 'error' 
                  ? 'bg-red-100 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                  : 'bg-yellow-100 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700'
              }`}>
                <p className={`text-sm ${
                  databaseStatus.database === 'error'
                    ? 'text-red-800 dark:text-red-200'
                    : 'text-yellow-800 dark:text-yellow-200'
                }`}>
                  <strong>{databaseStatus.database === 'error' ? `🚨 ${t('deals.columnSettings.databaseError')}` : `⚠️ ${t('deals.columnSettings.databaseIssue')}`}</strong> {databaseStatus.message}
                  <br />
                  {databaseStatus.database === 'error' 
                    ? t('deals.columnSettings.databaseUnavailable')
                    : t('deals.columnSettings.customColumnsUnavailable')
                  }
                </p>
                {databaseStatus.database === 'migration_needed' && (
                  <div className="mt-3">
                    <button
                      onClick={handleRunMigration}
                      disabled={migrating}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {migrating ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          Running Migration...
                        </>
                      ) : (
                        <>
                          🚀 {t('deals.columnSettings.runDatabaseMigration')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleResetToDefaults}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              disabled={saving}
            >
              {t('deals.columnSettings.resetToDefaults')}
            </button>
            {columns.filter(col => col.isCustom).length > 0 && (
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete ALL custom columns? This will remove all custom column data.')) {
                    handleResetToDefaults()
                  }
                }}
                className="px-4 py-2 text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/20 rounded-md hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                disabled={saving}
              >
                Clear All Custom
              </button>
            )}
            <button
              onClick={() => setShowAddColumn(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              {t('deals.columnSettings.addCustomColumn')}
            </button>
            <button
              onClick={handleSaveColumns}
              className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors flex items-center"
              disabled={saving}
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {t('deals.columnSettings.saveChanges')}
            </button>
          </div>
        </div>

        {/* Columns List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('deals.columnSettings.availableColumns')}
            </h2>
            
            <div className="space-y-3">
              {columns.map((column, index) => (
                <div
                  key={column.key}
                  className="flex items-center space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  {/* Visibility Toggle */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={column.visible}
                      onChange={() => handleToggleColumn(column.key)}
                      className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                    />
                  </div>

                  {/* Column Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {column.name}
                      </span>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        column.isCustom 
                          ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' 
                          : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                      }`}>
                        {column.isCustom ? t('deals.columnSettings.customColumns') : t('deals.columnSettings.system')}
                      </span>
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {column.type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Key: {column.key} | {t('deals.columnSettings.align')}: {column.align} | {column.sortable ? t('deals.columnSettings.sortable') : 'Not sortable'}
                      {column.type === 'dropdown' && column.dropdownOptions && column.dropdownOptions.length > 0 && (
                        <span className="ml-2">
                          | Options: {column.dropdownOptions.slice(0, 3).join(', ')}
                          {column.dropdownOptions.length > 3 && ` (+${column.dropdownOptions.length - 3} more)`}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Width & Alignment */}
                  <div className="flex items-center space-x-2 text-xs">
                    <select
                      value={column.align || 'left'}
                      onChange={(e) => {
                        const newColumns = columns.map(col =>
                          col.key === column.key
                            ? { ...col, align: e.target.value as 'left' | 'center' | 'right' }
                            : col
                        )
                        setColumns(newColumns)
                      }}
                      className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
                    >
                      <option value="left">{t('deals.columnSettings.left')}</option>
                      <option value="center">{t('deals.columnSettings.center')}</option>
                      <option value="right">{t('deals.columnSettings.right')}</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {/* Reorder */}
                    <div className="flex flex-col">
                      <button
                        onClick={() => handleReorderColumn(column.key, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleReorderColumn(column.key, 'down')}
                        disabled={index === columns.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {/* Edit (only for custom columns) */}
                    {column.isCustom && (
                      <button
                        onClick={() => setEditingColumn(column)}
                        className="p-1 text-gray-900 hover:text-blue-800 dark:text-gray-600 dark:hover:text-blue-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}

                    {/* Delete (only for custom columns) */}
                    {column.isCustom && (
                      <button
                        onClick={() => handleDeleteColumn(column)}
                        className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        disabled={saving}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Column Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('deals.columnSettings.totalColumns')}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{columns.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('deals.columnSettings.visibleColumns')}</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{columns.filter(col => col.visible).length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('deals.columnSettings.customColumns')}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-600">{columns.filter(col => col.isCustom).length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
                <svg className="h-6 w-6 text-gray-900 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Column Editor Modal */}
      <CustomColumnEditor
        isOpen={showAddColumn || editingColumn !== null}
        column={editingColumn}
        onSave={editingColumn ? handleEditColumn : handleAddColumn}
        onCancel={() => {
          setShowAddColumn(false)
          setEditingColumn(null)
        }}
        existingKeys={columns.map(col => col.key)}
      />
    </PageContent>
  )
}

export default ColumnSettings